<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Agent</span>
    </template>

    <template #actions>
      <router-link v-if="playId" :to="`/sales-plays/${playId}`" class="backLink">
        <AppIcon name="arrow_right" :size="14" class="backArrow" />
        Back to play
      </router-link>
      <span v-if="agent" class="muted" style="font-size:12px; margin-right:4px;">v{{ agent.current_version || 0 }}</span>
      <button class="btn secondary" @click="cancel" :disabled="canceling || saving || loading">{{ canceling ? 'Canceling…' : 'Cancel' }}</button>
      <button class="btn primary" @click="save" :disabled="saving || loading || !canSave">{{ saving ? 'Saving…' : (isNewInMemory ? 'Create' : 'Save') }}</button>
    </template>

    <div class="agentPage">

    <div v-if="error" class="errorBanner">{{ error }}</div>

    <div v-if="loading" class="pageLoading">Loading…</div>

    <div v-else-if="agent || isNewInMemory">

      <!-- Tab bar -->
      <div class="tabBar">
        <button
          class="tabBtn"
          :class="{ active: activeTab === 'definition' }"
          @click="activeTab = 'definition'"
        >Definition</button>
        <button
          v-if="agent"
          class="tabBtn"
          :class="{ active: activeTab === 'versions' }"
          @click="activeTab = 'versions'"
        >Versions</button>
        <button
          v-if="agent"
          class="tabBtn"
          :class="{ active: activeTab === 'runs' }"
          @click="activeTab = 'runs'"
        >Runs</button>
        <button
          v-if="agent"
          class="tabBtn"
          :class="{ active: activeTab === 'learning' }"
          @click="activeTab = 'learning'"
        >Learning</button>
      </div>

      <!-- Definition tab -->
      <div v-if="activeTab === 'definition'" class="definitionForm">
        <div class="formNameIconRow">
          <div class="formField" style="flex:1;">
            <label class="formLabel" for="agent-name">Name</label>
            <input id="agent-name" class="input" v-model="form.name" maxlength="200" />
          </div>
          <div class="formField">
            <label class="formLabel">Icon</label>
            <IconPicker
              v-model:iconName="iconName"
              v-model:iconStyle="iconStyle"
              v-model:iconColor="iconColor"
            />
          </div>
        </div>

        <div class="formField">
          <label class="formLabel" for="agent-approach">Approach</label>
          <textarea id="agent-approach" class="input approachInput" v-model="form.approach"></textarea>
          <div class="toolHintLine">
            Click here for a list of
            <button class="toolHintBtn toolHintInline" type="button" @click="showToolCatalogModal = true">tools</button>
            available to the agent
          </div>
        </div>
      </div>

      <!-- Versions tab -->
      <div v-if="activeTab === 'versions' && agent" class="page-layout">
        <div class="mainCol">
          <div class="panel">
            <div class="muted" style="font-size: 12px; margin-bottom: 10px;">
              Review saved versions, diff against previous, or promote an older version as the latest.
            </div>
            <div v-if="versionsLoading" class="muted">Loading versions…</div>
            <div v-else-if="versions.length === 0" class="muted">No version history yet.</div>
            <div v-else class="versionList">
              <div v-for="v in versions" :key="String(v.version)" class="versionCard">
                <div class="versionTopRow">
                  <div class="versionLabel">v{{ v.version }}</div>
                  <span v-if="Number(v.version) === Number(agent?.current_version || 0)" class="versionCurrentPill">current</span>
                </div>
                <div class="muted" style="font-size: 12px;">{{ formatTs(v.created_at) }}</div>
                <div class="muted" style="font-size: 12px;">By {{ versionActorLabel(v) }}</div>
                <div v-if="!isCurrentVersion(v) || previousVersionFor(v)" class="versionActions">
                  <button v-if="!isCurrentVersion(v)" class="btn secondary" type="button" @click="viewVersion(Number(v.version))">
                    View agent
                  </button>
                  <button
                    v-if="previousVersionFor(v)"
                    class="btn secondary"
                    type="button"
                    @click="openVersionDiff(previousVersionFor(v), Number(v.version))"
                  >
                    Diff vs previous
                  </button>
                  <button
                    v-if="!isCurrentVersion(v)"
                    class="btn secondary"
                    type="button"
                    :disabled="promotingVersion === Number(v.version)"
                    @click="promoteVersion(Number(v.version))"
                  >
                    {{ promotingVersion === Number(v.version) ? 'Promoting…' : 'Promote as latest' }}
                  </button>
                </div>
              </div>
            </div>
            <div v-if="versionsError" class="error" style="margin-top: 10px;">{{ versionsError }}</div>
          </div>
        </div>
      </div>

      <!-- Runs tab -->
      <div v-if="activeTab === 'runs' && agent" class="page-layout">
        <div class="mainCol">
          <div class="panel">
            <div v-if="runsLoading" class="muted">Loading runs…</div>
            <div v-else-if="runs.length === 0" class="muted">No runs yet.</div>
            <AgentRunsTable
              v-else
              :runs="runs"
              :nextOffset="runsNextOffset"
              :loading="runsLoading"
              :showBudgets="false"
              @openRun="openRun"
              @loadMore="loadRuns(false)"
            />
          </div>
        </div>
      </div>

      <!-- Learning tab: full-width -->
      <template v-if="activeTab === 'learning' && agent">

        <!-- Learning status banner (TOP) -->
        <div class="learningStatusBanner">
          <div class="learningStatusLeft">
            <div v-if="learningProfiles.length > 0" class="learningStatusRunInfo">
              <span class="learningStatusLabel">Last learning run:</span>
              <span v-for="p in learningProfiles" :key="p.reachout_type" class="lastRunItem">
                <span class="correctionType">{{ p.reachout_type }}</span>
                <span style="font-size: 11px;">{{ p.last_extraction_at ? formatTs(p.last_extraction_at) : 'never' }}</span>
              </span>
            </div>
            <div v-else style="font-size: 12px;">No learning run has happened yet.</div>
          </div>
          <div class="learningStatusRight">
            <span v-if="newCorrectionsCount > 0" class="learningStatusQueue">
              <span class="correctionNewBadge">{{ newCorrectionsCount }} new</span>
              <span style="font-size: 12px;">feedback item{{ newCorrectionsCount === 1 ? '' : 's' }} queued — need 5 to trigger a run</span>
            </span>
            <span v-else-if="corrections.length > 0" style="font-size: 12px; color: var(--muted);">All feedback has been evaluated.</span>
            <span v-else style="font-size: 12px; color: var(--muted);">No feedback yet.</span>
          </div>
        </div>

        <!-- Per-channel: rules panel -->
        <template v-for="profile in learningProfiles" :key="'rules-' + profile.reachout_type">
          <div class="panel" style="margin-bottom: 14px;">
            <div class="section-title">{{ channelLabel(profile.reachout_type) }} Learned Rules</div>
            <div v-if="profile.learned_rules && profile.learned_rules.length > 0" class="rulesTable" style="margin-top: 10px;">
              <div
                v-for="(rule, idx) in profile.learned_rules"
                :key="`rule-${idx}`"
                class="rulesRow"
                :class="{ editing: editingRule?.profileType === profile.reachout_type && editingRule?.idx === idx }"
              >
                <template v-if="editingRule?.profileType === profile.reachout_type && editingRule?.idx === idx">
                  <textarea class="input ruleEditInput" v-model="editingRule.text" rows="3" />
                  <div class="ruleActions">
                    <button class="btn primary" type="button" :disabled="learningsSaving" @click="saveEditedRule(profile)">Save</button>
                    <button class="btn secondary" type="button" @click="cancelEditRule">Cancel</button>
                  </div>
                </template>
                <template v-else>
                  <div class="ruleText">{{ rule.text }}</div>
                  <div class="ruleActions">
                    <span class="learningItemMeta">v{{ rule.agent_version }}</span>
                    <button class="learningEditBtn" type="button" :disabled="learningsSaving" @click="startEditRule(profile, idx, rule.text)">Edit</button>
                    <button class="learningDeleteBtn" type="button" :disabled="learningsSaving" @click="deleteRule(profile, idx)" title="Delete rule">✕</button>
                  </div>
                </template>
              </div>
            </div>
            <div v-else class="muted" style="font-size: 12px; margin-top: 8px;">No rules learned yet.</div>
            <div v-if="learningsError" class="error" style="margin-top: 8px;">{{ learningsError }}</div>
          </div>
        </template>

        <!-- Per-channel: sample messages panel -->
        <template v-for="profile in learningProfiles" :key="'samples-' + profile.reachout_type">
          <MasterDetailSplit
            :title="channelLabel(profile.reachout_type) + ' Sample Messages'"
            subtitle="Suggested message bodies the agent uses as style references."
            :items="(profile.learned_sample_reachouts || []).map((s, i) => ({ ...s, _idx: i }))"
            :selectedId="selectedSampleIdByType[profile.reachout_type] ?? '0'"
            :itemId="(s) => String(s._idx)"
            bodyMinHeight="260px"
            detailOverflow="auto"
            style="margin-bottom: 14px;"
            @select="(s) => onSampleSelect(profile, s)"
            @navigate="(dir) => onSampleNavigate(profile, dir)"
          >
            <template #empty-list>
              <div class="muted" style="padding: 14px;">No sample messages yet.</div>
            </template>
            <template #row="{ item: s }">
              <div class="sampleRowMeta">
                <span class="learningItemMeta">v{{ s.agent_version }}</span>
              </div>
              <div class="sampleRowPreview">{{ (s.body || '').slice(0, 100) }}</div>
            </template>
            <template #detail="{ selectedItem: s }">
              <div v-if="s" class="sampleDetailPane">
                <textarea
                  class="input sampleEditArea"
                  v-model="draftSampleBody"
                  placeholder="Sample email body…"
                />
                <div class="sampleDetailActions">
                  <button
                    class="btn primary"
                    type="button"
                    :disabled="learningsSaving || draftSampleBody === s.body"
                    @click="saveEditedSample(profile, s._idx)"
                  >Save</button>
                  <button
                    class="btn danger"
                    type="button"
                    :disabled="learningsSaving"
                    @click="deleteSample(profile, s._idx)"
                  >Delete</button>
                </div>
              </div>
            </template>
          </MasterDetailSplit>
        </template>

        <!-- Empty state when no profiles exist yet -->
        <div v-if="learningProfiles.length === 0" class="panel muted" style="margin-bottom: 14px;">
          No learnings yet — rules and samples appear once enough corrections have accumulated.
        </div>

        <!-- Recent corrections master-detail (SECOND) -->
        <MasterDetailSplit
          title="Recent Feedback"
          :subtitle="correctionsPanelSubtitle"
          :items="corrections"
          :selectedId="selectedCorrectionId"
          :itemId="(c) => String(c.id)"
          bodyMinHeight="420px"
          style="margin-top: 14px;"
          @select="(c) => { selectedCorrectionId = String(c.id) }"
          @navigate="onCorrectionNavigate"
        >
          <template #empty-list>
            <div v-if="correctionsLoading" class="muted">Loading…</div>
            <div v-else class="muted">No feedback yet. Edit reachouts before sending, or star exemplary ones.</div>
          </template>

          <template #row="{ item: c }">
            <div class="correctionMasterItemTop">
              <span class="correctionType">{{ c.reachout_type }}</span>
              <span style="display: flex; align-items: center; gap: 6px;">
                <span v-if="isCorrectionNew(c)" class="correctionNewBadge">new</span>
                <span class="muted" style="font-size: 11px;">{{ formatTs(c.sent_at) }}</span>
              </span>
            </div>
            <div class="correctionMasterSubject">{{ c.original_subject || c.subject }}</div>
            <div class="feedbackPills">
              <span v-if="c.starred" class="feedbackPill starred">
                <AppIcon name="star_filled" :size="12" style="color: #f59e0b;" />
                Starred
              </span>
              <span v-if="c.was_corrected" class="feedbackPill edited">
                <AppIcon name="email_setting" :size="12" />
                Edited
              </span>
            </div>
          </template>

          <template #detail="{ selectedItem: c }">
            <div v-if="c" style="padding: 14px;">

              <!-- Starred-only: no diff, show body as-is -->
              <template v-if="c.starred && !c.was_corrected">
                <div class="starredBanner" style="margin-bottom: 12px;">
                  <AppIcon name="star_filled" :size="16" style="color: #f59e0b;" />
                  <span>Starred as exemplary — sent as the AI drafted it. Weighted heavily for sample selection.</span>
                </div>
                <div class="correctionSubjectStatic" style="margin-bottom: 12px;">
                  <span class="correctionFieldLabel">Subject</span> {{ c.subject }}
                </div>
                <div>
                  <div class="correctionFieldLabel">Body</div>
                  <div class="panel starredBodyPreview">{{ c.body }}</div>
                </div>
              </template>

              <!-- Edited (with or without star): show diff -->
              <template v-else-if="c.was_corrected">
                <div v-if="c.starred" class="starredBanner" style="margin-bottom: 12px;">
                  <AppIcon name="star_filled" :size="16" style="color: #f59e0b;" />
                  <span>Starred and edited before sending — the final version is the exemplar.</span>
                </div>
                <div
                  v-if="c.original_subject && c.subject !== c.original_subject"
                  class="correctionSubjectDiff"
                  style="margin-bottom: 12px;"
                >
                  <div class="correctionFieldLabel">Subject</div>
                  <div class="correctionSubjectRow removed">
                    <span class="correctionDiffMark">−</span>
                    <span>{{ c.original_subject }}</span>
                  </div>
                  <div class="correctionSubjectRow added">
                    <span class="correctionDiffMark">+</span>
                    <span>{{ c.subject }}</span>
                  </div>
                </div>
                <div v-else class="correctionSubjectStatic" style="margin-bottom: 12px;">
                  <span class="correctionFieldLabel">Subject</span> {{ c.subject }}
                </div>
                <div v-if="c.original_body">
                  <div class="correctionFieldLabel">Body</div>
                  <LineDiff :before="c.original_body" :after="c.body" left-label="Original" right-label="Sent" />
                </div>
              </template>

              <!-- Legacy fallback -->
              <template v-else-if="c.original_body">
                <div class="correctionSubjectStatic" style="margin-bottom: 12px;">
                  <span class="correctionFieldLabel">Subject</span> {{ c.subject }}
                </div>
                <div>
                  <div class="correctionFieldLabel">Body</div>
                  <LineDiff :before="c.original_body" :after="c.body" left-label="Original" right-label="Sent" />
                </div>
              </template>

            </div>
          </template>
        </MasterDetailSplit>

      </template>

    </div>

    </div>

    <Modal v-if="showUnsavedModal" title="Unsaved changes" @close="dismissUnsaved">
      <div class="muted">You have unsaved changes. Would you like to save before leaving?</div>

      <template #footer>
        <button class="btn secondary" @click="dismissUnsaved">Stay on page</button>
        <button class="btn danger" @click="discardAndLeave" :disabled="saving">Discard</button>
        <button class="btn primary" @click="saveAndLeave" :disabled="saving || !isValid">{{ saving ? 'Saving…' : 'Save & leave' }}</button>
      </template>
    </Modal>

    <Modal v-if="versionDiffModal.open" title="Version diff" @close="closeVersionDiff">
      <div v-if="versionDiffLoading" class="muted">Loading diff…</div>
      <div v-else-if="!versionDiffModal.fromConfig || !versionDiffModal.toConfig" class="muted">No diff data.</div>
      <div v-else class="diffBody">
        <div v-if="iconChangedInDiff()" class="diffSection">
          <div class="diffSectionTitle">Icon changed</div>
          <div class="diffColumns">
            <div class="diffIconBox">
              <div class="diffMiniHead">Old (v{{ versionDiffModal.fromVersion }})</div>
              <HeroIcon
                :name="configIconName(versionDiffModal.fromConfig)"
                :iconStyle="configIconStyle(versionDiffModal.fromConfig)"
                :color="configIconColor(versionDiffModal.fromConfig)"
                :size="28"
              />
              <div class="muted">{{ configIconName(versionDiffModal.fromConfig) }} · {{ configIconStyle(versionDiffModal.fromConfig) }}</div>
            </div>
            <div class="diffIconBox">
              <div class="diffMiniHead">New (v{{ versionDiffModal.toVersion }})</div>
              <HeroIcon
                :name="configIconName(versionDiffModal.toConfig)"
                :iconStyle="configIconStyle(versionDiffModal.toConfig)"
                :color="configIconColor(versionDiffModal.toConfig)"
                :size="28"
              />
              <div class="muted">{{ configIconName(versionDiffModal.toConfig) }} · {{ configIconStyle(versionDiffModal.toConfig) }}</div>
            </div>
          </div>
        </div>

        <div v-if="approachChangedInDiff()" class="diffSection">
          <div class="diffSectionTitle">Approach diff</div>
          <LineDiff
            :before="String(versionDiffModal.fromConfig?.approach || '')"
            :after="String(versionDiffModal.toConfig?.approach || '')"
            left-label="Old"
            right-label="New"
          />
        </div>

        <div v-if="inputsChangedInDiff()" class="diffSection">
          <div class="diffSectionTitle">Inputs diff</div>
          <LineDiff
            :before="String(versionDiffModal.fromConfig?.inputs || '')"
            :after="String(versionDiffModal.toConfig?.inputs || '')"
            left-label="Old"
            right-label="New"
          />
        </div>

        <div v-if="otherChangesInDiff().length" class="diffSection">
          <div class="diffSectionTitle">Other changes</div>
          <div class="diffKeyList">
            <div v-for="(ch, idx) in otherChangesInDiff()" :key="`ch-${idx}`" class="diffKeyRow">
              <div class="diffKeyPath">{{ String(ch.path || '') }}</div>
              <div class="diffKeyCols">
                <div class="diffKeyCol">
                  <div class="diffMiniHead">Old</div>
                  <pre class="diffKeyVal">{{ formatDiffVal(ch.before) }}</pre>
                </div>
                <div class="diffKeyCol">
                  <div class="diffMiniHead">New</div>
                  <pre class="diffKeyVal">{{ formatDiffVal(ch.after) }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!iconChangedInDiff() && !approachChangedInDiff() && !inputsChangedInDiff() && !versionDiffModal.changes.length" class="muted">
          No differences.
        </div>
      </div>
      <template #footer>
        <button class="btn secondary" @click="closeVersionDiff">Close</button>
      </template>
    </Modal>

    <Modal v-if="showToolCatalogModal" title="Runtime Tools Reference" @close="showToolCatalogModal = false">
      <div class="muted" style="font-size: 12px; margin-bottom: 10px;">
        These are tools workspace agents can use during runs.
      </div>
      <div v-if="toolCatalogLoading" class="muted">Loading tools…</div>
      <div v-else-if="toolCatalogError" class="error">{{ toolCatalogError }}</div>
      <div v-else-if="groupedToolCatalog.length === 0" class="muted">No tool metadata available.</div>
      <div v-else class="toolCatalogList">
        <div v-for="group in groupedToolCatalog" :key="group.category" class="toolCategoryBlock">
          <div class="toolCategoryTitle">{{ categoryLabel(group.category) }}</div>
          <div class="toolPills">
            <span
              v-for="tool in group.items"
              :key="tool.key"
              class="toolPill"
              @click="toolDetailModal = { key: tool.key, description: tool.description || 'No description provided.' }"
            >{{ tool.key }}</span>
          </div>
        </div>
      </div>
      <template #footer>
        <button class="btn secondary" @click="showToolCatalogModal = false">Close</button>
      </template>
    </Modal>

    <Modal v-if="toolDetailModal" :title="toolDetailModal.key" @close="toolDetailModal = null">
      <p class="toolDetailDesc">{{ toolDetailModal.description }}</p>
      <template #footer>
        <button class="btn secondary" @click="toolDetailModal = null">Close</button>
      </template>
    </Modal>

    <Modal v-if="versionViewModal.open" :title="`Agent snapshot v${versionViewModal.version || ''}`" @close="closeVersionView">
      <div v-if="versionViewLoading" class="muted">Loading version…</div>
      <div v-else-if="versionViewError" class="error">{{ versionViewError }}</div>
      <div v-else-if="!versionViewModal.config" class="muted">No version data.</div>
      <div v-else class="versionViewBody">
        <div class="section-title">Definition</div>
        <div class="grid versionViewDefinition">
          <div class="nameIconRow">
            <div class="field" style="flex:1;">
              <label>Name</label>
              <input class="input versionViewReadOnly" :value="versionViewModal.config.name || ''" readonly />
            </div>
            <div class="field">
              <label>Icon</label>
              <div class="versionViewIconPreview">
                <HeroIcon
                  :name="configIconName(versionViewModal.config)"
                  :iconStyle="configIconStyle(versionViewModal.config)"
                  :color="configIconColor(versionViewModal.config)"
                  :size="22"
                />
              </div>
            </div>
          </div>

          <div class="field" style="grid-column: 1 / -1">
            <label>Approach</label>
            <textarea class="input approachInput versionViewReadOnly" :value="versionViewModal.config.approach || ''" readonly></textarea>
          </div>
        </div>
      </div>
      <template #footer>
        <button class="btn secondary" @click="closeVersionView">Close</button>
      </template>
    </Modal>
  </PageChrome>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import AgentRunsTable from '../../components/AgentRunsTable.vue'
import HeroIcon from '../../components/HeroIcon.vue'
import IconPicker from '../../components/IconPicker.vue'
import Modal from '../../components/Modal.vue'
import LineDiff from '../../components/LineDiff.vue'
import MasterDetailSplit from '../../components/MasterDetailSplit.vue'
import { apiFetch } from '../../services/api.ts'
import { useChatScope } from '../composables/useChatScope'

const route = useRoute()
const router = useRouter()

const { setScope, clearScope } = useChatScope()

const error = ref('')
const loading = ref(false)
const saving = ref(false)
const canceling = ref(false)

const showUnsavedModal = ref(false)
let pendingNavigation = null

const agent = ref(null)

const form = ref({
  name: '',
  approach: '',
})

const iconName = ref('UserIcon')
const iconStyle = ref('outline')
const iconColor = ref('#6b7280')

const runs = ref([])
const runsLoading = ref(false)
const runsNextOffset = ref(null)
const versions = ref([])
const versionsLoading = ref(false)
const versionsError = ref('')
const learningProfiles = ref([])
const learningsSaving = ref(false)
const learningsError = ref('')
const editingRule = ref(null) // { profileType, idx, text }
const selectedSampleIdByType = ref({})
const draftSampleBody = ref('')
const corrections = ref([])
const correctionsLoading = ref(false)

const lastExtractionByType = computed(() => {
  const map = {}
  for (const p of learningProfiles.value) {
    map[p.reachout_type] = p.last_extraction_at ?? null
  }
  return map
})

const newCorrectionsCount = computed(() => {
  return corrections.value.filter((c) => {
    const lastRun = lastExtractionByType.value[c.reachout_type]
    if (!lastRun) return true
    return new Date(c.sent_at) > new Date(lastRun)
  }).length
})

const correctionsPanelSubtitle = computed(() => {
  if (corrections.value.length === 0) return 'Reachouts the user edited before sending or starred as exemplary — the source material for learning.'
  if (newCorrectionsCount.value === 0) return 'All shown feedback has already been evaluated in a learning run.'
  return `${newCorrectionsCount.value} of ${corrections.value.length} feedback item${corrections.value.length === 1 ? '' : 's'} are new since the last learning run (need 5 to trigger).`
})

const CHANNEL_LABELS = {
  reachout_email: 'Email',
  reachout_call: 'Call',
  reachout_voicemail: 'Voicemail',
  reachout_linkedin_connect: 'LinkedIn Connect',
  reachout_linkedin_message: 'LinkedIn Message',
}

function channelLabel(type) {
  return CHANNEL_LABELS[type] ?? type
}

function isCorrectionNew(c) {
  const lastRun = lastExtractionByType.value[c.reachout_type]
  if (!lastRun) return true
  return new Date(c.sent_at) > new Date(lastRun)
}
const selectedCorrectionId = ref('')
const activeTab = ref('definition')
const promotingVersion = ref(null)
const versionDiffModal = ref({
  open: false,
  fromVersion: null,
  toVersion: null,
  changes: [],
  fromConfig: null,
  toConfig: null,
})
const versionDiffLoading = ref(false)
const showToolCatalogModal = ref(false)
const toolCatalogLoading = ref(false)
const toolCatalogError = ref('')
const runtimeToolCatalog = ref([])
const toolDetailModal = ref(null)
const versionConfigCache = ref({})
const versionViewLoading = ref(false)
const versionViewError = ref('')
const versionViewModal = ref({
  open: false,
  version: null,
  config: null,
})

// In the next UI the route is /sales-plays/:id/agent where :id is the
// PLAY id.  We fetch the play to resolve its agent_id.  If the play has
// no agent yet, the page enters create-new mode automatically.
const playId = computed(() => String(route.params.id || ''))
const resolvedAgentId = ref(null)
const agentId = computed(() => resolvedAgentId.value)

const isNewInMemory = computed(() => !resolvedAgentId.value)

const isValid = computed(() => {
  const nameOk = String(form.value?.name || '').trim()
  const approachOk = String(form.value?.approach || '').trim()
  return Boolean(nameOk && approachOk)
})

const lastSavedPayloadJson = ref('')

const currentPayloadJson = computed(() => {
  try {
    return JSON.stringify(buildAgentPayload())
  } catch {
    return ''
  }
})

const hasUnsavedChanges = computed(() => {
  if (!lastSavedPayloadJson.value) return false
  return currentPayloadJson.value !== lastSavedPayloadJson.value
})

const canSave = computed(() => {
  if (saving.value || loading.value) return false
  if (!isValid.value) return false
  return isNewInMemory.value ? true : hasUnsavedChanges.value
})

const groupedToolCatalog = computed(() => {
  const order = ['query', 'action', 'reachout', 'agent']
  const grouped = new Map()
  for (const item of runtimeToolCatalog.value || []) {
    const cat = String(item?.category || 'other').trim().toLowerCase() || 'other'
    if (!grouped.has(cat)) grouped.set(cat, [])
    grouped.get(cat).push(item)
  }
  const keys = [...grouped.keys()].sort((a, b) => {
    const ai = order.indexOf(a)
    const bi = order.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
  return keys.map((k) => ({ category: k, items: grouped.get(k) || [] }))
})

const versionNumberSet = computed(() => {
  const out = new Set()
  for (const v of versions.value || []) {
    const n = Number(v?.version || 0)
    if (n > 0) out.add(n)
  }
  return out
})

async function cancel() {
  if (hasUnsavedChanges.value) {
    pendingNavigation = () => router.push(`/sales-plays/${playId.value}`)
    showUnsavedModal.value = true
    return
  }
  router.push(`/sales-plays/${playId.value}`)
}

function dismissUnsaved() {
  showUnsavedModal.value = false
  pendingNavigation = null
}

function discardAndLeave() {
  showUnsavedModal.value = false
  lastSavedPayloadJson.value = currentPayloadJson.value
  const nav = pendingNavigation
  pendingNavigation = null
  if (nav) nav()
}

async function saveAndLeave() {
  await save()
  if (error.value) return
  showUnsavedModal.value = false
  const nav = pendingNavigation
  pendingNavigation = null
  if (nav) nav()
}

async function loadToolCatalog() {
  toolCatalogLoading.value = true
  toolCatalogError.value = ''
  try {
    const resp = await apiFetch('/agents/options')
    runtimeToolCatalog.value = Array.isArray(resp?.runtime_tool_catalog) ? resp.runtime_tool_catalog : []
  } catch (e) {
    runtimeToolCatalog.value = []
    toolCatalogError.value = e?.message || 'Failed to load tool reference'
  } finally {
    toolCatalogLoading.value = false
  }
}

function openRun(r) {
  const id = r?.id
  if (!id) return
  router.push(`/agent-runs/${encodeURIComponent(id)}`)
}

function formatTs(ts) {
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return ts
  }
}

function statusClass(status) {
  if (status === 'succeeded') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'running') return 'warning'
  if (status === 'queued') return 'warning'
  return ''
}

function versionActorLabel(v) {
  const email = String(v?.created_by_user_email || '').trim()
  const fallback = String(v?.created_by_user_id || '').trim()
  const src = String(v?.change_source || 'manual')
  const actor = email || (fallback ? `User ${fallback.slice(0, 8)}…` : 'Unknown')
  if (src === 'chat_agent') return `${actor} (Chat Agent)`
  return actor
}

function isCurrentVersion(v) {
  const current = Number(agent.value?.current_version || 0)
  return Number(v?.version || 0) === current
}

function previousVersionFor(v) {
  const n = Number(v?.version || 0)
  const prev = n - 1
  return prev > 0 && versionNumberSet.value.has(prev) ? prev : null
}

function configIconName(cfg) {
  return String(cfg?.icon_name || 'UserCircleIcon')
}

function configIconStyle(cfg) {
  return String(cfg?.icon_style || 'outline')
}

function configIconColor(cfg) {
  return String(cfg?.icon_color || '#6b7280')
}

function iconChangedInDiff() {
  const fromCfg = versionDiffModal.value.fromConfig || {}
  const toCfg = versionDiffModal.value.toConfig || {}
  return (
    String(fromCfg.icon_name || '') !== String(toCfg.icon_name || '') ||
    String(fromCfg.icon_style || '') !== String(toCfg.icon_style || '') ||
    String(fromCfg.icon_color || '') !== String(toCfg.icon_color || '')
  )
}

function approachChangedInDiff() {
  const fromText = String(versionDiffModal.value.fromConfig?.approach || '')
  const toText = String(versionDiffModal.value.toConfig?.approach || '')
  return fromText !== toText
}

function inputsChangedInDiff() {
  const fromText = String(versionDiffModal.value.fromConfig?.inputs || '')
  const toText = String(versionDiffModal.value.toConfig?.inputs || '')
  return fromText !== toText
}

function otherChangesInDiff() {
  const changes = Array.isArray(versionDiffModal.value?.changes) ? versionDiffModal.value.changes : []
  return changes.filter((c) => {
    const p = String(c?.path || '').trim()
    if (!p) return false
    if (p === 'approach') return false
    if (p === 'inputs') return false
    if (p === 'icon_name' || p === 'icon_style' || p === 'icon_color') return false
    return true
  })
}

function formatDiffVal(v) {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}

function categoryLabel(category) {
  const cat = String(category || '').trim().toLowerCase()
  if (cat === 'query') return 'Query Tools'
  if (cat === 'action') return 'Action Tools'
  if (cat === 'reachout') return 'Reachout Tools'
  if (cat === 'agent') return 'Agent Delegation Tools'
  if (cat === 'metadata') return 'Chat Tools'
  if (cat === 'sequence') return 'Sequence Tools'
  return 'Other Tools'
}

async function loadAgent() {
  loading.value = true
  error.value = ''
  try {
    if (isNewInMemory.value) {
      agent.value = null
      form.value = {
        name: 'New agent',
        approach: 'Steps to achieve the goal',
      }
      iconName.value = 'UserIcon'
      iconStyle.value = 'outline'
      iconColor.value = '#6b7280'
      try {
        lastSavedPayloadJson.value = JSON.stringify(buildAgentPayload())
      } catch {
        lastSavedPayloadJson.value = ''
      }
      return
    }
    const a = await apiFetch(`/agents/${agentId.value}`)
    agent.value = a
    form.value = {
      name: a.name || '',
      approach: a.approach || '',
    }

    iconName.value = a.icon_name || null
    iconStyle.value = a.icon_name ? (a.icon_style || 'outline') : null
    iconColor.value = a.icon_name ? (a.icon_color || null) : null

    try {
      lastSavedPayloadJson.value = JSON.stringify(buildAgentPayload())
    } catch {
      lastSavedPayloadJson.value = ''
    }

  } catch (e) {
    error.value = e?.message || 'Failed'
  } finally {
    loading.value = false
  }
}

async function loadRuns(reset = false) {
  runsLoading.value = true
  error.value = ''
  try {
    if (isNewInMemory.value) {
      runs.value = []
      runsNextOffset.value = null
      return
    }

    const off = reset ? 0 : Number(runsNextOffset.value || 0)
    if (!reset && !runsNextOffset.value) return

    const params = new URLSearchParams()
    params.set('limit', '50')
    params.set('offset', String(off))
    params.set('include_test', 'true')
    params.set('agent_id', String(agentId.value))

    const resp = await apiFetch(`/agent_runs?${params.toString()}`)
    const newRuns = resp?.runs || []
    runsNextOffset.value = resp?.next_offset ?? null
    runs.value = reset ? newRuns : [...runs.value, ...newRuns]
  } catch (e) {
    error.value = e?.message || 'Failed'
  } finally {
    runsLoading.value = false
  }
}

async function loadVersions() {
  versionsLoading.value = true
  versionsError.value = ''
  try {
    if (isNewInMemory.value || !agentId.value) {
      versions.value = []
      return
    }
    const resp = await apiFetch(`/agents/${encodeURIComponent(String(agentId.value))}/versions`)
    versions.value = Array.isArray(resp?.versions) ? resp.versions : []
  } catch (e) {
    versionsError.value = e?.message || 'Failed to load versions'
    versions.value = []
  } finally {
    versionsLoading.value = false
  }
}

async function fetchVersionConfig(version) {
  const v = Number(version || 0)
  if (!agentId.value || v <= 0) return null
  const cacheKey = String(v)
  const cached = versionConfigCache.value[cacheKey]
  if (cached && typeof cached === 'object') return cached
  const resp = await apiFetch(`/agents/${encodeURIComponent(String(agentId.value))}/versions/${encodeURIComponent(String(v))}`)
  const cfg = resp?.config && typeof resp.config === 'object' ? resp.config : {}
  versionConfigCache.value = {
    ...versionConfigCache.value,
    [cacheKey]: cfg,
  }
  return cfg
}

async function openVersionDiff(fromVersion, toVersion) {
  const fromV = Number(fromVersion || 0)
  const toV = Number(toVersion || 0)
  if (!agentId.value || fromV <= 0 || toV <= 0) return
  versionsError.value = ''
  versionDiffLoading.value = true
  versionDiffModal.value = {
    open: true,
    fromVersion: fromV,
    toVersion: toV,
    changes: [],
    fromConfig: null,
    toConfig: null,
  }
  try {
    const params = new URLSearchParams({
      from_version: String(fromV),
      to_version: String(toV),
    })
    const [resp, fromConfig, toConfig] = await Promise.all([
      apiFetch(`/agents/${encodeURIComponent(String(agentId.value))}/versions_diff?${params.toString()}`),
      fetchVersionConfig(fromV),
      fetchVersionConfig(toV),
    ])
    const changes = Array.isArray(resp?.changes) ? resp.changes : []
    versionDiffModal.value = {
      open: true,
      fromVersion: fromV,
      toVersion: toV,
      changes,
      fromConfig: fromConfig || {},
      toConfig: toConfig || {},
    }
  } catch (e) {
    versionsError.value = e?.message || 'Failed to load version diff'
    versionDiffModal.value = {
      open: false,
      fromVersion: null,
      toVersion: null,
      changes: [],
      fromConfig: null,
      toConfig: null,
    }
  } finally {
    versionDiffLoading.value = false
  }
}

function closeVersionDiff() {
  versionDiffModal.value = {
    open: false,
    fromVersion: null,
    toVersion: null,
    changes: [],
    fromConfig: null,
    toConfig: null,
  }
}

async function viewVersion(version) {
  const v = Number(version || 0)
  if (!agentId.value || v <= 0) return
  versionViewLoading.value = true
  versionViewError.value = ''
  versionViewModal.value = {
    open: true,
    version: v,
    config: versionConfigCache.value[String(v)] || null,
  }
  try {
    const resp = await apiFetch(`/agents/${encodeURIComponent(String(agentId.value))}/versions/${encodeURIComponent(String(v))}`)
    const cfg = resp?.config || null
    if (cfg && typeof cfg === 'object') {
      versionConfigCache.value = {
        ...versionConfigCache.value,
        [String(v)]: cfg,
      }
    }
    versionViewModal.value = {
      open: true,
      version: v,
      config: cfg,
    }
  } catch (e) {
    versionViewError.value = e?.message || 'Failed to load version'
  } finally {
    versionViewLoading.value = false
  }
}

function closeVersionView() {
  versionViewModal.value = {
    open: false,
    version: null,
    config: null,
  }
  versionViewError.value = ''
}

async function promoteVersion(version) {
  const v = Number(version || 0)
  if (!agentId.value || v <= 0) return
  promotingVersion.value = v
  versionsError.value = ''
  try {
    await apiFetch(`/agents/${encodeURIComponent(String(agentId.value))}/versions/${encodeURIComponent(String(v))}/promote`, {
      method: 'POST',
    })
    await Promise.all([loadAgent(), loadVersions(), loadRuns(true)])
  } catch (e) {
    versionsError.value = e?.message || 'Failed to promote version'
  } finally {
    promotingVersion.value = null
  }
}

async function loadCorrections() {
  if (isNewInMemory.value || !agentId.value) {
    corrections.value = []
    return
  }
  correctionsLoading.value = true
  try {
    const resp = await apiFetch(`/agents/${encodeURIComponent(String(agentId.value))}/recent_corrections`)
    corrections.value = Array.isArray(resp?.corrections) ? resp.corrections : []
    selectedCorrectionId.value = String(corrections.value[0]?.id ?? '')
  } catch {
    corrections.value = []
  } finally {
    correctionsLoading.value = false
  }
}

function onCorrectionNavigate(dir) {
  const items = corrections.value
  if (!items.length) return
  const idx = items.findIndex((c) => String(c.id) === selectedCorrectionId.value)
  if (dir === 'next') selectedCorrectionId.value = String(items[Math.min(idx + 1, items.length - 1)].id)
  else if (dir === 'prev') selectedCorrectionId.value = String(items[Math.max(idx - 1, 0)].id)
  else if (dir === 'first') selectedCorrectionId.value = String(items[0].id)
  else if (dir === 'last') selectedCorrectionId.value = String(items[items.length - 1].id)
}

async function loadLearningProfiles() {
  if (isNewInMemory.value || !agentId.value) {
    learningProfiles.value = []
    return
  }
  try {
    const resp = await apiFetch(`/agents/${encodeURIComponent(String(agentId.value))}/learning_profiles`)
    learningProfiles.value = Array.isArray(resp?.profiles) ? resp.profiles : []
  } catch {
    learningProfiles.value = []
  }
}

async function deleteRule(profile, ruleIndex) {
  learningsSaving.value = true
  learningsError.value = ''
  try {
    const updatedRules = (profile.learned_rules || []).filter((_, i) => i !== ruleIndex)
    const resp = await apiFetch(
      `/agents/${encodeURIComponent(String(agentId.value))}/learning_profiles/${encodeURIComponent(profile.reachout_type)}`,
      { method: 'PUT', body: { learned_rules: updatedRules } }
    )
    const idx = learningProfiles.value.findIndex((p) => p.reachout_type === profile.reachout_type)
    if (idx !== -1) learningProfiles.value[idx] = resp
  } catch (e) {
    learningsError.value = e?.message || 'Failed to delete rule'
  } finally {
    learningsSaving.value = false
  }
}

async function deleteSample(profile, sampleIndex) {
  learningsSaving.value = true
  learningsError.value = ''
  try {
    const updatedSamples = (profile.learned_sample_reachouts || []).filter((_, i) => i !== sampleIndex)
    const resp = await apiFetch(
      `/agents/${encodeURIComponent(String(agentId.value))}/learning_profiles/${encodeURIComponent(profile.reachout_type)}`,
      { method: 'PUT', body: { learned_sample_reachouts: updatedSamples } }
    )
    const idx = learningProfiles.value.findIndex((p) => p.reachout_type === profile.reachout_type)
    if (idx !== -1) learningProfiles.value[idx] = resp
  } catch (e) {
    learningsError.value = e?.message || 'Failed to delete sample'
  } finally {
    learningsSaving.value = false
  }
}

function startEditRule(profile, idx, text) {
  editingRule.value = { profileType: profile.reachout_type, idx, text }
}

function cancelEditRule() {
  editingRule.value = null
}

async function saveEditedRule(profile) {
  if (!editingRule.value) return
  learningsSaving.value = true
  learningsError.value = ''
  try {
    const updatedRules = (profile.learned_rules || []).map((r, i) =>
      i === editingRule.value.idx ? { ...r, text: editingRule.value.text } : r
    )
    const resp = await apiFetch(
      `/agents/${encodeURIComponent(String(agentId.value))}/learning_profiles/${encodeURIComponent(profile.reachout_type)}`,
      { method: 'PUT', body: { learned_rules: updatedRules } }
    )
    const idx = learningProfiles.value.findIndex((p) => p.reachout_type === profile.reachout_type)
    if (idx !== -1) learningProfiles.value[idx] = resp
    editingRule.value = null
  } catch (e) {
    learningsError.value = e?.message || 'Failed to save rule'
  } finally {
    learningsSaving.value = false
  }
}

function onSampleSelect(profile, sample) {
  selectedSampleIdByType.value = {
    ...selectedSampleIdByType.value,
    [profile.reachout_type]: String(sample._idx),
  }
  draftSampleBody.value = sample.body || ''
}

function onSampleNavigate(profile, dir) {
  const items = (profile.learned_sample_reachouts || []).map((s, i) => ({ ...s, _idx: i }))
  if (!items.length) return
  const currentId = selectedSampleIdByType.value[profile.reachout_type] ?? '0'
  const idx = items.findIndex((s) => String(s._idx) === currentId)
  let newIdx = idx
  if (dir === 'next') newIdx = Math.min(idx + 1, items.length - 1)
  else if (dir === 'prev') newIdx = Math.max(idx - 1, 0)
  else if (dir === 'first') newIdx = 0
  else if (dir === 'last') newIdx = items.length - 1
  const newItem = items[newIdx]
  selectedSampleIdByType.value = {
    ...selectedSampleIdByType.value,
    [profile.reachout_type]: String(newItem._idx),
  }
  draftSampleBody.value = newItem.body || ''
}

async function saveEditedSample(profile, sampleIndex) {
  learningsSaving.value = true
  learningsError.value = ''
  try {
    const updatedSamples = (profile.learned_sample_reachouts || []).map((s, i) =>
      i === sampleIndex ? { ...s, body: draftSampleBody.value } : s
    )
    const resp = await apiFetch(
      `/agents/${encodeURIComponent(String(agentId.value))}/learning_profiles/${encodeURIComponent(profile.reachout_type)}`,
      { method: 'PUT', body: { learned_sample_reachouts: updatedSamples } }
    )
    const idx = learningProfiles.value.findIndex((p) => p.reachout_type === profile.reachout_type)
    if (idx !== -1) learningProfiles.value[idx] = resp
  } catch (e) {
    learningsError.value = e?.message || 'Failed to save sample'
  } finally {
    learningsSaving.value = false
  }
}

async function resolveAgentFromPlay() {
  if (!playId.value) return
  try {
    const resp = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}`)
    const play = resp?.play || resp
    const sequenceAction = (play?.actions || []).find((a) => a.action_type === 'sequence')
    resolvedAgentId.value = String(sequenceAction?.definition?.agent?.id || '').trim() || null
  } catch {
    resolvedAgentId.value = null
  }
}

async function reload() {
  await resolveAgentFromPlay()
  await loadToolCatalog()
  await Promise.all([loadAgent(), loadRuns(true), loadVersions(), loadLearningProfiles(), loadCorrections()])
}

function buildAgentPayload() {
  return {
    ...form.value,
    shared: true,
    icon_name: iconName.value,
    icon_style: iconStyle.value,
    icon_color: iconColor.value,
  }
}

async function save() {
  if (!canSave.value) return
  saving.value = true
  error.value = ''
  try {
    let updated
    if (isNewInMemory.value) {
      const body = { ...buildAgentPayload(), sales_play_id: playId.value || undefined }
      updated = await apiFetch('/agents', { method: 'POST', body })
      // Now that the agent exists, update our resolved ID so the page
      // switches from create to edit mode seamlessly.
      resolvedAgentId.value = String(updated.id)
    } else {
      updated = await apiFetch(`/agents/${agentId.value}`, { method: 'PUT', body: buildAgentPayload() })
    }
    agent.value = updated
    iconName.value = updated.icon_name || null
    iconStyle.value = updated.icon_name ? (updated.icon_style || 'outline') : null
    iconColor.value = updated.icon_name ? (updated.icon_color || null) : null

    try {
      lastSavedPayloadJson.value = JSON.stringify(buildAgentPayload())
    } catch {
      lastSavedPayloadJson.value = ''
    }
    await loadVersions()
  } catch (e) {
    error.value = e?.message || 'Failed'
  } finally {
    saving.value = false
  }
}

onBeforeRouteLeave((to, from, next) => {
  if (!hasUnsavedChanges.value) return next()
  pendingNavigation = () => router.push(to.fullPath)
  showUnsavedModal.value = true
  next(false)
})

watch(agentId, async () => {
  await reload()
})

function onBeforeUnload(e) {
  if (hasUnsavedChanges.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

onMounted(async () => {
  window.addEventListener('beforeunload', onBeforeUnload)
  await reload()
  setScope({ scopeType: 'sales_play', scopeSubjectId: playId.value, pageContext: { sales_play_id: playId.value, page_type: 'agent' } })
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', onBeforeUnload)
  clearScope()
})
</script>

<style scoped>
/* Legacy token bridge — maps legacy vars to next design tokens so
   child components (MasterDetailSplit, etc.) inherit the new palette. */
.agentPage {
  --border: var(--color-border-subtle);
  --border2: var(--color-border-subtle);
  --surface: var(--color-bg-surface);
  --text: var(--color-text-primary);
  --muted: var(--color-text-tertiary);
  --primary: var(--color-accent);
  --success: var(--color-success);
  --danger: var(--color-danger);
  --badge-bg: var(--color-bg-subtle);
  --row-hover-bg: var(--color-bg-subtle);
  --hover-bg: var(--color-bg-subtle);
  --link: var(--color-accent);
  --panel: var(--color-bg-surface);
  --field-bg: var(--color-bg-subtle);
  --table-header-bg: var(--color-bg-subtle);
  --focus-border: var(--color-accent);
  font-family: var(--font-sans);
}

.eyebrow {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.backLink:hover { color: var(--color-accent); }
.backArrow { transform: rotate(180deg); }

.error { color: var(--danger); margin-bottom: 10px; }
.muted { color: var(--muted); }

.errorBanner {
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-4);
  background: var(--color-danger-soft);
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-md);
  font-size: var(--fs-sm);
  color: var(--color-danger);
}

.pageLoading { color: var(--color-text-tertiary); font-size: var(--fs-sm); }

.definitionForm {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-width: 720px;
}

.formField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.formLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}

.formNameIconRow {
  display: flex;
  align-items: flex-end;
  gap: var(--space-3);
}

.runRow {
  cursor: pointer;
}

.runRow:hover {
  background: var(--row-hover-bg);
}

.page-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
}

.mainCol {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel {
  min-width: 0;
}

.section-title {
  font-weight: 600;
  margin-bottom: 10px;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.nameIconRow {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  grid-column: 1 / -1;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.approachInput {
  min-height: 220px;
}

label { font-size: 12px; color: var(--muted); }

.meta {
  display: flex;
  gap: 12px;
  margin-top: 10px;
}

.versionList {
  display: grid;
  gap: 8px;
}

.versionCard {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  display: grid;
  gap: 6px;
}

.versionTopRow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.versionLabel {
  font-weight: 700;
}

.versionCurrentPill {
  font-size: 11px;
  border-radius: 999px;
  border: 1px solid var(--selection-border);
  color: var(--link);
  background: var(--selection-bg);
  padding: 2px 8px;
}

.versionActions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.diffBody {
  display: grid;
  gap: 12px;
}

.diffColumns {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
}

.diffCol {
  min-width: 0;
}

.diffColHead {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  margin-bottom: 6px;
}

.diffKeyList {
  display: grid;
  gap: 10px;
}

.diffKeyRow {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  background: var(--surface);
}

.diffKeyPath {
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 8px;
}

.diffKeyCols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.diffKeyVal {
  margin: 0;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--badge-bg);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 12px;
}

.diffAgentCard {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.diffAgentName {
  font-weight: 600;
}

.diffSection {
  display: grid;
  gap: 8px;
}

.diffSectionTitle {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
}

.diffMiniHead {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
}

.diffIconBox {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px;
  display: grid;
  justify-items: start;
  gap: 6px;
}


.toolCatalogList {
  display: grid;
  gap: 10px;
  max-height: 60vh;
  overflow: auto;
}

.toolCategoryBlock {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  display: grid;
  gap: 8px;
}

.toolCategoryTitle {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.toolPills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.toolPill {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 11px;
  background: var(--surface2, #f3f4f6);
  border: 1px solid var(--line, #e5e7eb);
  border-radius: 20px;
  padding: 3px 10px;
  cursor: pointer;
  white-space: nowrap;
  color: var(--text);
}

.toolPill:hover {
  background: var(--surface3, #e5e7eb);
  border-color: var(--border2, #d1d5db);
}

.toolDetailDesc {
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}

.toolHintBtn {
  border: 0;
  background: transparent;
  color: var(--link);
  font-size: 12px;
  padding: 0;
  margin-top: 2px;
  text-align: left;
  cursor: pointer;
}

.toolHintBtn:hover {
  text-decoration: underline;
}

.toolHintLine {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

.toolHintInline {
  margin-top: 0;
  font-size: inherit;
  display: inline;
  vertical-align: baseline;
}

.versionViewBody {
  display: grid;
  gap: 8px;
  font-size: 13px;
}

.versionViewDefinition {
  margin-top: 2px;
}

.versionViewReadOnly {
  background: var(--table-header-bg);
}

.versionViewIconPreview {
  width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: var(--badge-bg);
  cursor: default;
}

.tabBar {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 14px;
}

.tabBtn {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
  margin-bottom: -1px;
}

.tabBtn:hover {
  color: var(--text);
}

.tabBtn.active {
  color: var(--text);
  border-bottom-color: var(--link);
}

.correctionMasterItemTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.correctionMasterSubject {
  font-size: 12px;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.correctionType {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
  background: var(--surface2, #f3f4f6);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 1px 6px;
}

.learningStatusBanner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
  background: color-mix(in srgb, var(--link, #2563eb) 8%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--link, #2563eb) 30%, transparent);
  border-radius: 8px;
  color: var(--text);
}

.learningStatusLeft {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.learningStatusRight {
  display: flex;
  align-items: center;
  gap: 8px;
}

.learningStatusLabel {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: color-mix(in srgb, var(--link, #2563eb) 80%, var(--text));
}

.learningStatusRunInfo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.learningStatusQueue {
  display: flex;
  align-items: center;
  gap: 6px;
}

.lastRunItem {
  display: flex;
  align-items: center;
  gap: 4px;
}

.correctionNewBadge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #c05000;
  background: #fff3e0;
  border: 1px solid #f0a060;
  border-radius: 4px;
  padding: 1px 5px;
}

.feedbackPills {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.feedbackPill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  white-space: nowrap;
}

.feedbackPill.starred {
  background: #fef3c7;
  color: #92400e;
}

.feedbackPill.edited {
  background: var(--color-bg-subtle, #f1f5f9);
  color: var(--color-text-secondary, #334155);
}

.starredBanner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 12px;
  color: #92400e;
}

.starredBodyPreview {
  white-space: pre-wrap;
  line-height: 1.5;
  font-size: 13px;
}

.correctionFieldLabel {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.correctionSubjectDiff {
  display: grid;
  gap: 2px;
}

.correctionSubjectRow {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.correctionSubjectRow.removed {
  background: var(--diff-removed-bg, #fff1f2);
}

.correctionSubjectRow.added {
  background: var(--diff-added-bg, #f0fdf4);
}

.correctionDiffMark {
  font-weight: 700;
  font-family: ui-monospace, monospace;
  flex-shrink: 0;
  width: 12px;
}

.correctionSubjectStatic {
  font-size: 13px;
  color: var(--text);
}


.rulesTable {
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

.rulesRow {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
}

.rulesRow:last-child {
  border-bottom: none;
}

.rulesRow.editing {
  grid-template-columns: 1fr;
  align-items: start;
  padding: 10px;
}

.ruleText {
  line-height: 1.4;
}

.ruleActions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.ruleEditInput {
  width: 100%;
  resize: vertical;
}

.learningEditBtn {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--muted);
  cursor: pointer;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  line-height: 1.4;
}

.learningEditBtn:hover:not(:disabled) {
  color: var(--text);
  border-color: var(--border2, #d1d5db);
}

.learningEditBtn:disabled {
  opacity: 0.4;
  cursor: default;
}

.sampleRowMeta {
  margin-bottom: 2px;
}

.sampleRowPreview {
  font-size: 12px;
  color: var(--text);
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.sampleDetailPane {
  padding: 12px;
  display: grid;
  gap: 10px;
}

.sampleEditArea {
  min-height: 180px;
  resize: vertical;
  font-size: 13px;
  line-height: 1.5;
}

.sampleDetailActions {
  display: flex;
  gap: 8px;
}

.learningItemMeta {
  font-size: 11px;
  color: var(--muted);
  white-space: nowrap;
  margin-top: 2px;
}

.learningDeleteBtn {
  background: transparent;
  border: none;
  color: var(--muted);
  cursor: pointer;
  font-size: 11px;
  padding: 2px 4px;
  flex-shrink: 0;
  line-height: 1;
}

.learningDeleteBtn:hover:not(:disabled) {
  color: var(--danger);
}

.learningDeleteBtn:disabled {
  opacity: 0.4;
  cursor: default;
}


</style>
